import type { AccountListsResponse } from '@/shared/api/graphql/account-lists';
import { CREATE_ACCOUNT_LIST, GET_ALL_ACCOUNTS_LIST } from '@/shared/api/graphql/account-lists';
import useShowMedicalServices from '@/shared/lib/hooks/useShowMedicalServices';
import useToken from '@/shared/lib/hooks/useToken';
import { MedicalServices, Role } from '@/shared/lib/types/admin';
import { getPatientIdFromPath, TAG_STARRED } from '@/shared/lib/util/constants';
import { pathnameIncludes, pathnameIncludesAny } from '@/shared/lib/util/location';
import { SideMenuContext } from '@/shared/model';
import { useOrganizationStarredStore } from '@/shared/model/OrganizationStarredStore';
import { useSidebarStore } from '@/shared/model/SidebarStore';
import { theme } from '@/shared/styles';
import MainSideMenu from '@/shared/ui/components/SideMenus/MainSideMenu';
import { NAV_COLORS } from '@/shared/ui/components/SideMenus/navPalette';
import SidebarMenuTabs from '@/shared/ui/components/SideMenus/SidebarMenuTabs';
import SidebarPaneHeader from '@/shared/ui/components/SideMenus/SidebarPaneHeader';
import SidebarRailDock from '@/shared/ui/components/SideMenus/SidebarRailDock';
import { useMutation, useQuery } from '@apollo/client';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicationIcon from '@mui/icons-material/Medication';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PersonIcon from '@mui/icons-material/Person';
import type { StackProps } from '@mui/material';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/** Width of the always-visible icon rail.  The collapsed nav is exactly this wide. */
export const SIDE_NAV_RAIL_WIDTH = 72;

/**
 * Width of the link list itself.  It sits beside the rail rather than sharing its space, so the
 * rail never narrows the menu and the list is the same width whatever the icons do.
 *
 * Sized to the longest thing in it -- the "Transitional Care Management" heading at ~196px, plus
 * the row's padding and its collapse chevron -- rather than to a round number, which is where the
 * dead space to the right of the links came from.
 */
export const SIDE_NAV_MENU_WIDTH = 256;

/** What the nav occupies with the menu open. */
export const SIDE_NAV_EXPANDED_WIDTH = SIDE_NAV_RAIL_WIDTH + SIDE_NAV_MENU_WIDTH;

interface IStyledSideMenuIconButton {
  isSelected?: boolean;
  height?: string;
}

const StyledSideMenuIconButton = styled(IconButton, {
  shouldForwardProp: prop => isPropValid(prop)
})((props: IStyledSideMenuIconButton) => ({
  color: props.isSelected === true ? NAV_COLORS.inkIcon : NAV_COLORS.onDark,
  backgroundColor: props.isSelected === true ? NAV_COLORS.onDark : NAV_COLORS.rail,
  width: '100%',
  padding: theme.spacing(1),
  borderRadius: 0,
  height: props.height ?? theme.spacing(6),
  '&:hover': {
    backgroundColor: props.isSelected === true ? NAV_COLORS.onDark : NAV_COLORS.hover,
    color: props.isSelected === true ? NAV_COLORS.inkIcon : NAV_COLORS.onDark
  },
  '&:disabled': {
    color: 'inherit',
    opacity: 0.5
  }
}));

type Props = StackProps & {
  drawerOpen: boolean;
};
export const BaseSideMenuSideNav = ({ children, drawerOpen, ...props }: Props) => {
  const { roleType } = useToken();
  const navigate = useNavigate();
  const { drawerToggle, setDrawerOpen, isMobileOrTablet } = useContext(SideMenuContext);
  const location = useLocation();
  const { pathname } = location;

  // A patient in the path is what gives the pane a second menu to offer.
  const patientId = getPatientIdFromPath(pathname);
  const menuTab = useSidebarStore(state => state.menuTab);
  const setMenuTab = useSidebarStore(state => state.setMenuTab);
  const onPatientTab = Boolean(patientId) && menuTab === 'patient';

  // Opening a different chart starts on that patient again rather than wherever it was left.
  useEffect(() => {
    if (patientId) setMenuTab('patient');
  }, [patientId, setMenuTab]);

  const isRPMViewable = useShowMedicalServices(MedicalServices.RPM);
  const isMTMViewable = useShowMedicalServices(MedicalServices.MTM);
  const isTCMViewable = useShowMedicalServices(MedicalServices.TCM);

  const isProviderAdmin = useMemo(() => roleType === Role.PROVIDER, [roleType]);
  const isPharmacist = useMemo(() => roleType === Role.PHARMACIST, [roleType]);
  const isAnyAdmin = useMemo(
    () => roleType === Role.SUPER || roleType === Role.PHARMACIST_ADMIN || roleType === Role.ADT_ADMIN,
    [roleType]
  );
  // Matches the Reports section's own gate, so the rail button never leads somewhere empty.
  // Declared after isAnyAdmin on purpose: reading it earlier is a temporal-dead-zone throw.
  const canSeeReports = isAnyAdmin || roleType === Role.ADMIN || isProviderAdmin || isPharmacist;

  const { setStarredList } = useOrganizationStarredStore();

  // create and get default account list for organization
  const { data: accountListData, refetch: refetchAccountList } = useQuery<AccountListsResponse>(GET_ALL_ACCOUNTS_LIST, {
    variables: {
      pageNum: 1,
      pageSize: 100
    },
    fetchPolicy: 'cache-and-network'
  });
  const [createAccountList] = useMutation(CREATE_ACCOUNT_LIST, {
    onCompleted: () => refetchAccountList()
  });

  // Mobile/tablet: close the overlay drawer after every navigation (including navigations to the
  // same pathname, e.g. query-param-only links) so it never lingers open over the new page.
  useEffect(() => {
    if (isMobileOrTablet) {
      setDrawerOpen(false);
    }
  }, [location.key, isMobileOrTablet]);

  useEffect(() => {
    if (accountListData && accountListData.getAllAccountLists) {
      const accountList = accountListData.getAllAccountLists?.accountLists?.find(accountList => accountList?.name === TAG_STARRED);
      if (accountList) return setStarredList(accountList);

      createAccountList({
        variables: {
          name: TAG_STARRED,
          description: TAG_STARRED,
          isShared: false
        }
      })
        .then(() => refetchAccountList())
        .catch(err => console.error(err));
    }
  }, [accountListData]);

  return (
    <Stack
      direction="row"
      {...props}
      sx={[
        {
          height: '100%'
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx])
      ]}
    >
      <Stack
        sx={{
          bgcolor: NAV_COLORS.rail,
          height: '100%',
          width: SIDE_NAV_RAIL_WIDTH,
          flexShrink: 0
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto'
          }}
        >
          <Tooltip title={drawerOpen ? 'Collapse menu' : 'Expand menu'} placement="right" disableInteractive>
            <StyledSideMenuIconButton onClick={drawerToggle} isSelected={false}>
              {drawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </StyledSideMenuIconButton>
          </Tooltip>
          <Tooltip title="Back" placement="right" disableInteractive>
            <StyledSideMenuIconButton
              onClick={() => {
                navigate(-1);
              }}
              disabled={window.history.state?.idx === 0}
              isSelected={false}
            >
              <ArrowBackIosNewIcon />
            </StyledSideMenuIconButton>
          </Tooltip>
          {isRPMViewable && (
            <Tooltip title="Care Management" placement="right" disableInteractive>
              <StyledSideMenuIconButton
                onClick={() => {
                  navigate(`/care-management/patients`);
                }}
                isSelected={pathnameIncludes('/care-management')}
              >
                <MonitorHeartIcon />
              </StyledSideMenuIconButton>
            </Tooltip>
          )}
          {isRPMViewable && (
            <Tooltip title="Provider" placement="right" disableInteractive>
              <StyledSideMenuIconButton
                onClick={() => {
                  navigate(`/remote-patient-monitoring/patient-schedules`);
                }}
                isSelected={pathnameIncludesAny(['/remote-patient-monitoring/patient-schedules', '/care-management/workqueue'])}
              >
                <LocalHospitalIcon />
              </StyledSideMenuIconButton>
            </Tooltip>
          )}
          <Tooltip title="Patients Management" placement="right" disableInteractive>
            <StyledSideMenuIconButton
              onClick={() => {
                navigate(`/patients/lookup`);
              }}
              isSelected={pathnameIncludes('/patients/lookup')}
            >
              <PersonIcon />
            </StyledSideMenuIconButton>
          </Tooltip>
          {isMTMViewable && (
            <Tooltip title="Medications Management" placement="right" disableInteractive>
              <StyledSideMenuIconButton
                onClick={() => {
                  navigate(`/tasks`);
                }}
                isSelected={pathnameIncludes('/tasks')}
              >
                <MedicationIcon />
              </StyledSideMenuIconButton>
            </Tooltip>
          )}
          {isAnyAdmin && isTCMViewable && (
            <Tooltip title="Transitional Care Management" placement="right" disableInteractive>
              <StyledSideMenuIconButton
                onClick={() => {
                  navigate(`/transitional-care-management/dashboard`);
                }}
                isSelected={pathnameIncludes('/transitional-care-management')}
              >
                <AssignmentIndIcon />
              </StyledSideMenuIconButton>
            </Tooltip>
          )}
          {canSeeReports && (
            <Tooltip title="Reports" placement="right" disableInteractive>
              <StyledSideMenuIconButton
                onClick={() => {
                  // Admins land on the org dashboard; everyone else on the one report they all have.
                  navigate(isAnyAdmin ? `/reports/dashboard` : `/reports/rpm-metrics`);
                }}
                isSelected={pathnameIncludes('/reports')}
              >
                <AssessmentIcon />
              </StyledSideMenuIconButton>
            </Tooltip>
          )}
        </Stack>

        {/*
          Outside the scrolling list on purpose.  Site Map belongs at the foot of the rail, and a
          short viewport scrolls the end of that list out of sight -- so it sits here instead,
          pinned directly above the dock's divider and always reachable.
        */}
        <Stack sx={{ alignItems: 'center', flexShrink: 0, paddingBottom: 0.5 }}>
          <Tooltip title="Site Map" placement="right" disableInteractive>
            <StyledSideMenuIconButton onClick={() => navigate('/sitemap')} isSelected={pathnameIncludes('/sitemap')}>
              <AccountTreeIcon />
            </StyledSideMenuIconButton>
          </Tooltip>
        </Stack>

        <SidebarRailDock />
      </Stack>
      {drawerOpen && (
        <Box
          sx={{
            width: SIDE_NAV_MENU_WIDTH,
            flexShrink: 0,
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: onPatientTab ? NAV_COLORS.paneAlt : NAV_COLORS.pane,
            // Hover follows the pane: the drawer paper cannot know which tab is showing.
            '--nav-hover': onPatientTab ? NAV_COLORS.paneAltHover : NAV_COLORS.hover
          }}
        >
          <SidebarPaneHeader />
          {patientId && <SidebarMenuTabs value={menuTab} onChange={setMenuTab} />}

          {patientId && menuTab === 'main' ? <MainSideMenu /> : children}
        </Box>
      )}
    </Stack>
  );
};
